import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { formatDistanceToNow } from "date-fns"
import { ptBR } from "date-fns/locale"
import { ExternalLink, LogIn, X } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { startImpersonation } from "../actions/hijack-actions"
import { useRecentUsersStore } from "../store/recent-users-store"
import type { RecentUserAccessType } from "../store/recent-users-store"

function accessLabel(type: RecentUserAccessType): string {
  return type === "hijack" ? "Entrou como" : "Detalhe"
}

export function RecentUsersCards() {
  const navigate = useNavigate()
  const entries = useRecentUsersStore(state => state.entries)
  const clear = useRecentUsersStore(state => state.clear)

  if (entries.length === 0) {
    return null
  }

  const handleOpen = (id: string) => {
    navigate(`/admin/users/${id}`)
  }

  const handleHijack = async (id: string) => {
    try {
      await startImpersonation(id)
      window.location.href = "/dashboard"
    } catch {
      toast.error("Erro ao acessar como usuário")
    }
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-muted-foreground text-sm font-medium">Últimos acessados</h3>
        <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={clear}>
          <X className="h-3 w-3" />
          Limpar
        </Button>
      </div>

      <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-2">
        {entries.map(entry => (
          <Card
            key={entry.id}
            className="flex w-[220px] shrink-0 flex-col gap-2 px-3 py-3 shadow-xs"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium" title={entry.name}>
                  {entry.name}
                </p>
                <p className="text-muted-foreground truncate text-xs" title={entry.email}>
                  {entry.email}
                </p>
              </div>
              <Badge
                variant={entry.lastAccessType === "hijack" ? "default" : "secondary"}
                className="shrink-0 text-[10px]"
              >
                {accessLabel(entry.lastAccessType)}
              </Badge>
            </div>

            {entry.organizationName && (
              <p className="text-muted-foreground truncate text-xs" title={entry.organizationName}>
                {entry.organizationName}
              </p>
            )}

            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground text-[10px]">
                {formatDistanceToNow(new Date(entry.lastAccessedAt), {
                  addSuffix: true,
                  locale: ptBR,
                })}
              </span>
              <div className="flex items-center gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      onClick={() => handleOpen(entry.id)}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Abrir detalhe</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      onClick={() => handleHijack(entry.id)}
                    >
                      <LogIn className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Entrar como usuário</TooltipContent>
                </Tooltip>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </section>
  )
}
